import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { TreeNode, ComponentType, Unit } from '@/types/bom';

// Simplified BOM component type with only the fields we need
interface BOMComponentBasic {
  id: string;
  bom_id: string;
  parent_component_id?: string;
  component_name: string;
  component_type: ComponentType;
  material?: string;
  drawing_number?: string; // Added drawing_number
  quantity: number;
  unit: Unit | null;
  level: number;
  sequence_order?: number;
  specifications?: any; // To hold operations, etc.
}

export function useBOMHierarchy() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Function to transform flat components into a hierarchical tree structure
  const buildComponentTree = (components: BOMComponentBasic[]): TreeNode[] => {
    const map = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    // First pass: Create all nodes
    components.forEach(component => {
      const node: TreeNode = {
        id: component.id,
        component_name: component.component_name,
        component_type: component.component_type,
        level: component.level,
        quantity: component.quantity,
        unit: component.unit,
        material: component.material,
        drawing_number: component.drawing_number, // Added drawing_number
        parent_id: component.parent_component_id || null,
        children: []
      };
      map.set(component.id, node);
    });

    // Second pass: Connect nodes to create the tree
    components.forEach(component => {
      const node = map.get(component.id);
      if (node) {
        if (component.parent_component_id && map.has(component.parent_component_id)) {
          // This is a child node, add it to its parent
          const parent = map.get(component.parent_component_id);
          if (parent) {
            parent.children.push(node);
          }
        } else {
          // This is a root node (no parent)
          roots.push(node);
        }
      }
    });

    return roots;
  };

  // Fetch BOM components from the database for a BOM ID
  const getBOMComponentsHierarchy = useCallback(async (idValue: string, isProjectId = false) => {
    if (!idValue) return { flatComponents: [], hierarchyTree: [] };
    
    setIsLoading(true);
    setError(null);

    try {
      if (isProjectId) {
        console.log("Treating as Project ID:", idValue);
        
        // First check if there are BOMs associated with this project
        const { data: projectBoms, error: projectBomsError } = await supabase
          .from('boms')
          .select('id, name')
          .eq('project_id', idValue);
          
        if (projectBomsError) {
          console.error("Error fetching project BOMs:", projectBomsError);
          setError(projectBomsError as Error);
          return { flatComponents: [], hierarchyTree: [] };
        }
        
        console.log("Found project BOMs:", projectBoms);
        
        if (!projectBoms || projectBoms.length === 0) {
          console.log("No BOMs found for project");
          return { flatComponents: [], hierarchyTree: [] };
        }
        
        // Use the first BOM for now - in the future we might want to let the user select
        const bomId = projectBoms[0].id;
        console.log("Using BOM ID:", bomId);
        
        // Now use the regular BOM hierarchy logic with this BOM ID
        return await getBOMComponentsHierarchy(bomId);
      }
      
      console.log("Fetching hierarchy for BOM ID:", idValue);
      
      // Step 1: Fetch all units from the UOMS table and create a map
      const { data: unitsData, error: unitsError } = await supabase
        .from('uoms')
        .select('id, name, code, category, is_base_uom, description');

      if (unitsError) {
        console.error('Error fetching UOMs:', unitsError);
        throw unitsError;
      }

      const unitsMap = new Map<string, Unit>();
      if (unitsData) {
        for (const unit of unitsData) {
          unitsMap.set(unit.id, unit);
        }
      }

      // Step 2: Fetch all BOM components
      const { data: componentsData, error: componentsError } = await supabase
        .from('bom_components')
        .select('id, bom_id, parent_component_id, component_name, component_type, material, drawing_number, quantity, level, sequence_order, unit, specifications')
        .eq('bom_id', idValue)
        .order('sequence_order', { ascending: true });

      if (componentsError) {
        console.error('Error fetching BOM components:', componentsError);
        throw componentsError;
      }

      console.log('Loaded flat components:', componentsData?.length ?? 0);

      // Step 3: Map components and join units data on the client-side
      const components = (componentsData || []).map(item => {
        return {
          id: item.id,
          bom_id: item.bom_id,
          parent_component_id: item.parent_component_id,
          component_name: item.component_name,
          component_type: item.component_type as ComponentType,
          material: item.material,
          drawing_number: item.drawing_number,
          quantity: item.quantity,
          unit: item.unit ? unitsMap.get(item.unit) || null : null,
          level: item.level || 0,
          sequence_order: item.sequence_order,
          specifications: item.specifications,
        };
      }) as BOMComponentBasic[];

      const hierarchyTree = buildComponentTree(components);

      return {
        flatComponents: components,
        hierarchyTree
      };
    } catch (err) {
      console.error('Error fetching BOM components hierarchy:', err);
      setError(err as Error);
      return { flatComponents: [], hierarchyTree: [] };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    getBOMComponentsHierarchy,
    isLoading,
    error
  };
}
